import { Component, OnInit, ViewChildren, QueryList, OnDestroy } from '@angular/core';
import { DashboardWidgetBasicComponent } from '../../dashboard-widget-basic/dashboard-widget-basic.component';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { CommunicationService } from 'src/app/services/communication';
import { ParameterHandlerService, CacheService } from 'projects/common/src/app/services';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { IMinuteByMinute } from 'src/app/modules/widget/interfaces/minute-by-minute';
import { NgbdSortableHeaderDirective, SortEvent } from '../../../directives/ngbdSortableHeader.directive';
import { compare, compareDate, numberOnly } from 'projects/common/src/app/services/utils/utilities';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';

@Component({
  selector: 'app-train-minute-by-minute',
  templateUrl: 'train-minute-by-minute.component.html',
})
export class TrainMinuteByMinuteComponent extends DashboardWidgetBasicComponent implements OnInit, OnDestroy {
  public minutesData: IMinuteByMinute[];
  private originalData: IMinuteByMinute[];

  public column = '';
  public direction: string;

  private firstLoad = true;
  public displayTable = false;
  public displayMessage: boolean;

  public amountPages;
  public displayBackIcon = false;
  public displayRightIcon = false;
  public displayInputPage;

  // current page
  private _page: number;
  get page(): number {
    return this._page;
  }
  set page(value: number) {
    this._page = value;
  }

  // number of records to display
  public _pageSize: number;
  get pageSize(): number {
    return this._pageSize;
  }
  set pageSize(value: number) {
    this._pageSize = value;
  }

  private _inputPage;
  get inputPage(): number {
    return this._inputPage;
  }
  set inputPage(value: number) {
    this._inputPage = value;
  }

  // total
  public collectionSize;

  @ViewChildren(NgbdSortableHeaderDirective) private headers: QueryList<NgbdSortableHeaderDirective>;

  constructor(
    public booleanBarService: BooleanStateService,
    public communication: CommunicationService,
    public parameterService: ParameterHandlerService,
    public cacheService: CacheService,
    public machineContext: MachineContextService,
    protected screenViewService: ScreenViewService,
  ) {
    super(booleanBarService, communication, parameterService, cacheService, machineContext, screenViewService);
    this.displayMessage = false;
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
    };
  }

  // eslint-disable-next-line
    public ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * build view to reload when date is changed
   *
   * @param values default value
   */
  public buildView(values: any): void {
    super.buildView(values);
    this.buildData(values);
  }

  /**
   * Get Data from Api train
   */
  private buildData(minuteByMinuteData: IMinuteByMinute[]): void {
    this.displayMessage = !minuteByMinuteData.length;
    this.collectionSize = minuteByMinuteData.length;
    this.originalData = [...minuteByMinuteData];

    this.column = '';
    this.direction = '';

    // resetting other headers
    this.headers.forEach((header) => {
      header.direction = '';
    });

    this.pageSize = 10;
    this.page = 1;
    this.inputPage = 1;
    this.amountPages = this.getAmountPage();

    this.firstLoad = false;

    // put data to the table
    this.pageData(this.sort(this.column, this.direction));
  }

  /**
   * paginate data for table
   *
   * @param data data to paginate
   */
  private pageData(data: IMinuteByMinute[]): void {
    data = data || [];
    this.displayTable = !!data.length;
    this.minutesData = data.slice((this.page - 1) * this.pageSize, (this.page - 1) * this.pageSize + this.pageSize);

    this.displayPaginationIcons();
  }

  /**
   * handler to display or not icon to go back and forward page
   */
  private displayPaginationIcons(): void {
    if (this.page === 1) {
      this.displayBackIcon = false;
    }
    if (this.page <= this.amountPages && this.page !== 1) {
      this.displayBackIcon = true;
    }

    if (!this.minutesData.length) {
      this.displayRightIcon = false;
    } else if (this.minutesData.length) {
      if (this.page < this.amountPages) {
        this.displayRightIcon = true;
      } else if (this.page >= this.amountPages) {
        this.displayRightIcon = false;
      }
    }

    if (this.amountPages === 1) {
      this.displayInputPage = false;
    } else {
      this.displayInputPage = true;
    }
  }

  /**
   * Handle sort event click from table headers
   *
   * @param column header of the table
   * @param direction asc or desc
   */
  public onSort({ column, direction }: SortEvent): void {
    this.column = column;
    this.direction = direction;

    // resetting other headers
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    this.pageData(this.sort(this.column, this.direction));
  }

  /**
   * Sort table
   *
   * @param column header of the table
   * @param direction asc or desc
   */
  private sort(column: string, direction: string): IMinuteByMinute[] {
    const arr = this.originalData ? [...this.originalData] : [];

    if (direction === '') {
      return arr;
    }
    if (column !== 'completedAt') {
      return arr.sort((a, b) => {
        const res = compare(a[column], b[column]);
        return direction === 'asc' ? res : -res;
      });
    }
    return arr.sort((a, b) => {
      const res = compareDate(a[column], b[column]);
      return direction === 'asc' ? res : -res;
    });
  }

  /**
   * listen paginator click event
   */
  public advancePage(): void {
    if (this.page < this.amountPages) {
      this.page += 1;
      this.inputPage = this.page;
      this.setPage();
    }
  }

  /**
   * listen paginator click event
   */
  public goBackPage(): void {
    if (this.page !== 1 && this.page > 1) {
      this.page -= 1;
      this.inputPage = this.page;
      this.setPage();
    }
  }

  /**
   * get the pages of the paginator
   */
  private getAmountPage(): number {
    const amount = Math.ceil(this.collectionSize / this.pageSize);

    // if the current page selected exceeds the total page number
    if (this.page >= amount) {
      this.page = amount;
      this.inputPage = amount;
    }
    return amount;
  }

  /**
   * change the number of recors to display
   */
  public changePageSize(): void {
    this.amountPages = this.getAmountPage();
    this.pageData(this.sort(this.column, this.direction));
  }

  /**
   * set the page number to display
   */
  public setPage(): void {
    this.inputPage = +this.inputPage; // convert string to number

    if (this.firstLoad === false) {
      if (!Number.isNaN(this.inputPage) && this.inputPage > 0) {
        if (this.inputPage <= this.amountPages) {
          this.page = this.inputPage;
          this.pageData(this.sort(this.column, this.direction));
        }
      }
    }
  }

  /**
   * input accept only numbers
   *
   * @param input event
   */
  public numberOnly(event): boolean {
    return numberOnly(event);
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
  }
}
